import React, { useRef, useEffect, useState } from 'react';
import './ImageViewer.css';

function ImageViewer({ imageUrl, detections = [], selectedIndex = null, onDetectionClick, metadata = null }) {
  const canvasRef = useRef(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  
  useEffect(() => {
    if (!imageUrl) return;
    
    const img = new Image();
    img.onload = () => {
      setImageDimensions({ width: img.width, height: img.height });
      drawImage(img);
    };
    img.src = imageUrl;
  }, [imageUrl, detections, selectedIndex, metadata]);
  
  function drawImage(img) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Set canvas size to match image
    canvas.width = img.width;
    canvas.height = img.height;
    
    // Draw image
    ctx.drawImage(img, 0, 0);
    
    // Draw only selected detection
    if (selectedIndex !== null && selectedIndex >= 0 && selectedIndex < detections.length) {
      const det = detections[selectedIndex];
      
      console.log('📦 Detection (model pixels, CENTER format):', { 
        cx: det.cx?.toFixed(0), 
        cy: det.cy?.toFixed(0), 
        w: det.width.toFixed(0), 
        h: det.height.toFixed(0) 
      });
      
      let x, y, w, h;
      
      if (metadata && det.cx !== undefined) {
        // Convert from model CENTER coordinates (with letterbox) to original image top-left coordinates
        const { scale, padX, padY } = metadata;
        
        console.log('🔧 Metadata:', { scale, padX, padY, imgSize: `${img.width}x${img.height}` });
        
        // Remove padding from center coordinates and scale back
        const cx_original = (det.cx - padX) / scale;
        const cy_original = (det.cy - padY) / scale;
        const w_original = det.width / scale;
        const h_original = det.height / scale;
        
        // Convert center to top-left
        x = cx_original - w_original / 2;
        y = cy_original - h_original / 2;
        w = w_original;
        h = h_original;
        
        console.log('📍 Original coords (top-left):', { 
          x: x.toFixed(0), 
          y: y.toFixed(0), 
          w: w.toFixed(0), 
          h: h.toFixed(0) 
        });
      } else {
        // Fallback
        x = det.x || 0;
        y = det.y || 0;
        w = det.width;
        h = det.height;
      }
      
      // Draw bbox
      ctx.strokeStyle = '#00FF00'; // Green
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, w, h);
    } else {
      console.log('⚠️ No detection selected or invalid index:', selectedIndex);
    }
  }
  
  function handleCanvasClick(e) {
    if (!onDetectionClick || detections.length === 0) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Get click position relative to canvas
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;
    
    // Find clicked detection
    for (let i = detections.length - 1; i >= 0; i--) {
      const det = detections[i];
      
      let detX, detY, detW, detH;
      if (metadata && det.cx !== undefined) {
        const { scale, padX, padY } = metadata;
        const cx_original = (det.cx - padX) / scale;
        const cy_original = (det.cy - padY) / scale;
        const w_original = det.width / scale;
        const h_original = det.height / scale;
        detX = cx_original - w_original / 2;
        detY = cy_original - h_original / 2;
        detW = w_original;
        detH = h_original;
      } else {
        detX = det.x || 0;
        detY = det.y || 0;
        detW = det.width;
        detH = det.height;
      }
      
      if (clickX >= detX && clickX <= detX + detW && clickY >= detY && clickY <= detY + detH) {
        onDetectionClick(i);
        return;
      }
    }
    
    // Clicked outside any detection
    onDetectionClick(null);
  }
  
  return (
    <div className="image-viewer">
      <canvas
        ref={canvasRef}
        className="detection-canvas"
        onClick={handleCanvasClick}
        style={{ cursor: onDetectionClick ? 'pointer' : 'default' }}
      />
    </div>
  );
}

export default ImageViewer;

